import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { SxProps, Theme } from "@mui/material/styles";
import { FilterRange } from "../../Ranges/types";
import React, { useCallback, useEffect, useState } from "react";
import { VendorInstallsCountFilter } from "../VendorInstallsCountFilter";

interface VendorInstallsCountSectionProps {
  disabled?: boolean;
  productCount?: FilterRange;
  onProductCountChange: (value: FilterRange) => void;
  id: string;
  sx?: SxProps<Theme>;
}

/**
 * Reusable vendor installs count filter section component
 * Encapsulates the checkbox + conditional range input pattern
 */
export const VendorInstallsCountSection: React.FC<VendorInstallsCountSectionProps> =
  React.memo(({ disabled = false, productCount, onProductCountChange, sx }) => {
    const hasValues =
      productCount &&
      (productCount.min !== undefined || productCount.max !== undefined);
    const [filterEnabled, setFilterEnabled] = useState(Boolean(hasValues));

    // Only auto-enable when values are added, don't auto-disable when cleared
    useEffect(() => {
      if (hasValues) {
        setFilterEnabled(true);
      }

      if (hasValues === false && disabled) {
        setFilterEnabled(false);
      }
    }, [hasValues, disabled]);

    const handleToggle = useCallback(() => {
      setFilterEnabled((prev) => {
        const newEnabled = !prev;

        // Clear selection when disabling
        if (!newEnabled) {
          onProductCountChange({ min: undefined, max: undefined });
        }

        return newEnabled;
      });
    }, [onProductCountChange]);

    return (
      <Box sx={sx}>
        {/* Checkbox */}
        <Stack direction="row" alignItems="center">
          <FormControlLabel
            control={
              <Checkbox
                checked={filterEnabled}
                onChange={handleToggle}
                disabled={disabled}
                size="small"
              />
            }
            label="Refine by number of installs from these vendors"
          />
        </Stack>

        {/* Filter Input */}
        {filterEnabled && !disabled && (
          <Box my={1}>
            <VendorInstallsCountFilter
              value={productCount}
              onChange={onProductCountChange}
              isDisabled={disabled}
            />
          </Box>
        )}
      </Box>
    );
  });
